<template>
  <div class="case-logic-container old-globals">
    <help-tooltip
      v-if="advancedControls && errors.length"
      class="caseLogicWarning"
      type="error"
    >
      <span v-for="error in errors" :key="error.message">
        {{ error.message }}
        <br>
      </span>
    </help-tooltip>
    <help-tooltip
      v-if="advancedControls && warnings.length"
      class="caseLogicWarning"
      type="warning"
    >
      <span v-for="warning in warnings" :key="warning.message">
        {{ warning.message }}
        <br>
      </span>
    </help-tooltip>
    <transition name="fade">
      <div v-if="state.clipboardWarning" class="popup-warning">
        Nothing to Paste
      </div>
      <div v-if="state.undoredoWarning" class="popup-warning">
        Nothing to Undo/Redo
      </div>
    </transition>

    <div class="case-logic-inner-container">
      <!-- The inner container was added to prevent the toolbar and zoom controls
      from being affected by canvas translations caused by popper.js -->
      <div
        :id="`case-logic-${editorId || caseConf.id}`"
        class="case-logic"
        :class="cursorClass"
        @mousedown="mouseDown()"
        @mouseup="mouseUp()"
      />
      <div v-show="toolbarsVisible" class="zoom-controls">
        <md-button class="md-icon-button md-dense" @click="zoomFull">
          <md-icon>
            open_with
          </md-icon>
        </md-button>
        <md-button class="md-icon-button md-dense" @click="zoomOut">
          <md-icon>remove</md-icon>
        </md-button>
        <span>&nbsp;{{ state.zoom ? (state.zoom * 100).toFixed(0) : '--' }}%</span>
        <md-button class="md-icon-button md-dense" @click="zoomIn">
          <md-icon>add</md-icon>
        </md-button>
      </div>
      <case-logic-toolbar
        v-show="toolbarsVisible"
        :active-tool="state.activeTool"
        :readonly="readonly"
        :get-preference="getPreference"
        @setPreference="setPreference"
        @setActiveTool="setActiveTool"
        @addGroup="addGroup"
        @undo="undo"
        @redo="redo"
      />
    </div>
    <md-dialog
      :md-active="state.effectChainModalOpened"
      :md-close-on-esc="true"
      class="effect-chain-dialog"
      @md-clicked-outside="closeEffectChain"
    >
      <md-dialog-title>Effect Chain</md-dialog-title>
      <md-dialog-content>
        <effect-chain-editor
          v-if="state.effectChainModalOpened && !!state.openEffectChainNode"
          :id="state.openEffectChainNode.data.effectChain.id"
          :effect-chain="state.openEffectChainNode.data.effectChain"
          :case-conf="caseConf"
          :user="user"
          :readonly="readonly"
          @update="effectChain => updateEffectChainNode(effectChain)"
        />
      </md-dialog-content>
      <md-dialog-actions>
        <md-button class="md-primary md-raised" @click="closeEffectChain">
          Close
        </md-button>
      </md-dialog-actions>
    </md-dialog>
    <el-card v-if="advancedControls" class="additional-controls">
      <div class="additional-controls-collapser">
        <md-button
          class="md-icon-button"
          @click="toggleAdditionalControlsBar()"
        >
          <md-icon>
            {{
              state.additionalControlsPanelOpen ? 'last_page' : 'first_page'
            }}
          </md-icon>
        </md-button>
      </div>
      <div
        v-show="state.additionalControlsPanelOpen"
        class="additional-controls-content"
      >
        <el-button
          :icon="state.advancedControlsPanelOpen ? 'caret-top' : 'caret-bottom'"
          @click="toggleAdvancedControlsPanel"
        >
          Advanced
        </el-button>
        <div v-if="state.advancedControlsPanelOpen" class="separator" />
        <custom-states-panel
          v-if="state.advancedControlsPanelOpen"
          :custom-states="state.customStates"
          :readonly="readonly"
          :emitter="editor"
          @update="customStates => updateCustomStates(customStates)"
        />
        <div v-if="state.advancedControlsPanelOpen" class="separator" />
        <case-logic-browser
          v-if="editor"
          :case-logic-description="caseLogicDescription"
          :case-logic-editor="editor"
          @onSelect="({ node }) => navigateToNode(node)"
        />
        <div class="settings">
          Settings
          <md-radio v-model="state.lineType" value="curve">
            Smooth Lines
            <img class="line-type" src="./assets/icons/smooth-lines.svg">
          </md-radio>
          <md-radio v-model="state.lineType" value="curveLinear">
            Angled Lines
            <img class="line-type" src="./assets/icons/angled-lines.svg">
          </md-radio>
        </div>
      </div>
    </el-card>
    <div v-show="state.loading" class="loading-panel md-overlay">
      <div class="hardware-accelerated-spinner">
        <div class="spinner-ring" />
      </div>
    </div>
  </div>
</template>

<script>
import Rete from 'rete';
import ConnectionPlugin from 'rete-connection-plugin';
import VueRenderPlugin from 'rete-vue-render-plugin';
import ContextMenuPlugin from 'rete-context-menu-plugin';
import ConnectionPathPlugin from 'rete-connection-path-plugin';
import AreaPlugin from 'rete-area-plugin';
import ReadonlyPlugin from 'rete-readonly-plugin';
import $ from 'jquery';
import debounce from 'lodash/debounce';
import get from 'lodash/get';
import invoke from 'lodash/invoke';
import uniqBy from 'lodash/uniqBy';
import isEmpty from 'lodash/isEmpty';
import snakeCase from 'lodash/snakeCase';
import last from 'lodash/last';
import HistoryPlugin from 'rete-history-plugin';

import HelpTooltip from '../common/components/HelpTooltip';
import ActionComponent from './nodes/ActionComponent';
import EffectComponent from './nodes/EffectComponent';
import CheckComponent from './nodes/CheckComponent';
import TimerComponent from './nodes/TimerComponent';
import FrameComponent from './nodes/FrameComponent';
import CommentComponent from './nodes/CommentComponent';
import EffectChainComponent from './nodes/EffectChainComponent';
import EffectChainEditor from './EffectChainEditor';

import CustomStatesPanel from './CustomStatesPanel';
import IsolatedIdPlugin from './utils/isolated-id-plugin';
import NodesBBoxPlugin from './utils/NodesBBoxPlugin';
import LassoSelectPlugin from './utils/lasso-select-plugin';
import GroupPlugin from './utils/group-plugin';
import LodPlugin from './utils/LodPlugin';
import CaseLogicBrowser from './components/CaseLogicBrowser.vue';
import { copyNodesToClipboard, pasteNodesFromClipboard } from './utils/clipboard/clipboard';
import { enableHistoryTracking } from './utils/history/trackNodeHistory';
import CaseLogicToolbar from './components/CaseLogicToolbar.vue';

export default {
  name: 'case-logic',
  components: {
    CustomStatesPanel,
    EffectChainEditor,
    HelpTooltip,
    CaseLogicBrowser,
    CaseLogicToolbar,
  },
  props: {
    editorId: {
      type: String,
      default: null,
    },
    caseConf: {
      type: Object,
      required: true,
    },
    user: {
      type: Object,
      default: () => ({}),
    },
    availableNodes: {
      type: Array,
      default: () => ['Action', 'Check', 'Effect', 'EffectChain', 'Timer', 'Frame', 'Group', 'Comment'],
    },
    advancedControls: {
      type: Boolean,
      default: true,
    },
    readonly: {
      type: Boolean,
      default: false,
    },
    isGlobal: {
      type: Boolean,
      default: false,
    },
    // Whether this is the root case logic
    // that validation errors should be reported on
    // or an effect chain case logic
    // Unfortunately validation error node ids
    // are not namespaced to the case logic they occurred in
    // so they can bleed inside effect chains and get reported
    // on nodes because of clashing ids
    isRoot: {
      type: Boolean,
      default: false,
    },
    navigation: {
      type: Object,
      default: () => null,
    },
  },
  data() {
    return {
      state: {
        customStates: [],
        additionalControlsPanelOpen: this.getPreference('additionalControlsPanelOpen') === false ? false : true,
        advancedControlsPanelOpen: false,
        openEffectChainNode: null,
        effectChainModalOpened: false,
        showWarnings: true,
        showErrors: true,
        lastUpdateIsInternal: false,
        pendingReset: true,
        dragDisabledNodes: {},
        loading: true,
        activeTool: this.readonly ? 'grab' : 'pointer',
        previousActiveTool: this.readonly ? 'grab' : 'pointer',
        isMouseDown: false,
        spaceOrMiddleModeSwitch: false,
        zoom: null,
        lineType: this.getPreference('lineType') || 'curve',
        groupLasso: false,
        clipboardWarning: false,
        undoredoWarning: false,
        // LOD (Level of Detail) settings
        lodEnabled: this.getPreference('lodEnabled') !== false,
        lodViewportCulling: this.getPreference('lodViewportCulling') !== false,
        lodZoomSimplification: this.getPreference('lodZoomSimplification') !== false,
      },
      editor: null,
    };
  },
  computed: {
    caseLogicDescription() {
      return this.caseConf.caseLogicDescription || {};
    },
    isAdmin() {
      return this.user['https://vrpatients.com/role'] === 'SUPER_ADMIN';
    },
    isMilitaryOrg() {
      return this.user['https://vrpatients.com/productType'] === 'MILITARY';
    },
    errors() {
      if (!this.$store) {
        return [];
      }
      const errors = get(
        this.$store.getters['validation/errorsByCategory'],
        'caseLogic',
        [],
      ).filter(error => !error.id);

      return uniqBy(errors, 'message');
    },
    warnings() {
      if (!this.$store) {
        return [];
      }
      const warnings = get(
        this.$store.getters['validation/visibleWarningsByCategory'],
        'caseLogic',
        [],
      ).filter(warning => !warning.id);

      return uniqBy(warnings, 'message');
    },
    cursorClass() {
      if (this.state.isMouseDown && this.state.activeTool === 'grab') {
        return 'active-tool-grabbing';
      }
      return `active-tool-${this.state.activeTool}`;
    },
    toolbarsVisible() {
      return !this.state.effectChainModalOpened;
    },
    canEdit() {
      return !this.readonly && this.state.activeTool !== 'grab';
    }
  },
  watch: {
    readonly(newValue) {
      this.setReadOnly(newValue);
    },
    'state.activeTool'(activeTool) {
      if (activeTool === 'lasso') {
        this.editor.trigger('lassoEnable');
      } else {
        this.editor.trigger('lassoDisable');
      }
      this.editor.activeTool = activeTool;
    },
    caseConf(newVal) {
      this.editor.caseConf = newVal;
    },
    async navigation(navigation) {
      if (navigation.category !== 'caseLogic') {
        return;
      }
      if (!navigation.params || !navigation.params.nodeId) {
        return;
      }
      const node = this.editor.nodes.find(n => n.id === navigation.params.nodeId);
      if (!node) {
        return;
      }
      AreaPlugin.zoomAt(this.editor, [node]);
      this.editor.selectNode(node);
    },
    'state.lineType'(lineType) {
      this.editor.trigger('updateconnectionoptions', this.getLineTypeOptions(lineType));
      this.setPreference('lineType', lineType);
    },
    'state.additionalControlsPanelOpen'(additionalControlsPanelOpen) {
      this.setPreference('additionalControlsPanelOpen', additionalControlsPanelOpen);
    }
  },
  created() {
    if (this.caseConf && this.caseConf.caseLogicDescription) {
      this.state.customStates =
        this.caseConf.caseLogicDescription.customStates || [];
    }

    if (!this.isGlobal) {
      this.$eventHub.$on('CASE::FLUSH_PENDING_CHANGES', this.update);
    }
  },
  async mounted() {
    setTimeout(async () => {
      const { caseConf } = this;
      const editorId = this.editorId || caseConf.id;
      const container = document.querySelector(`#case-logic-${editorId}`);

      const mouse = { x: 0, y: 0 };

      if (!container) {
        return;
      }
      const editor = new Rete.NodeEditor(`${editorId}@0.1.0`, container);
      // Need to listen the undo/redo events before the history plugin so we can
      // show the warning when history is empty

      editor.on('undo', () => {
        if (editor.history.produced.length === 0) {
          this.showUndoRedoWarning();
        }
      });

      editor.on('redo', () => {
        if (editor.history.reserved.length === 0) {
          this.showUndoRedoWarning();
        }
      })

      // This is the best way I could find to determine if an object is a
      // DragNodeAction.  Including DragNodeAction and using instanceof doesn't
      // work.  It will work as long as DragNodeAction is the only NodeAction
      // subclass that implements a _translate method.
      const isDragNodeAction = (obj => !!obj.undo && !!obj._translate)

      // Rete triggers `nodedraged` events on mouse up after a translate
      // Unfortunately this does not happen when the translate is simply "redone"
      // However some plugins depend on that `nodedraged` event (e.g. the group-plugin)
      // So we need to force it to trigger after a translate undo/redo
      editor.on('undo', () => {
        const actionToUndo = last(editor.history.produced);
        if (actionToUndo && isDragNodeAction(actionToUndo)) {
          setTimeout(() => {
            editor.trigger('nodedraged', actionToUndo.node);
          })
        }
      });

      editor.on('redo', () => {
        const actionToRedo = last(editor.history.reserved);
        if (actionToRedo && isDragNodeAction(actionToRedo)) {
          setTimeout(() => {
            editor.trigger('nodedraged', actionToRedo.node);
          })
        }
      });

      editor.use(HistoryPlugin, { keyboard: true });
      Object.assign(editor, {
        customStates: this.state.customStates,
        showWarnings: this.state.showWarnings,
        showErrors: this.state.showErrors,
        caseConf: this.caseConf,
        isAdmin: this.isAdmin,
        isMilitaryOrg: this.isMilitaryOrg,
        readonly: this.readonly,
        $store: this.isRoot ? this.$store : null,
      });
      this.editor = editor;

      editor.use(ConnectionPlugin);
      editor.use(VueRenderPlugin);
      editor.use(ContextMenuPlugin, {
        allocate(component) {
          if (component.name !== 'Group') {
            return [];
          }
          return undefined;
        },
        items: {
          Group: () => {
            this.state.groupLasso = true;
            this.setActiveTool('lasso');
          },
          Paste: async () => {
            try {
              const updates = await pasteNodesFromClipboard(editor, this.caseConf, mouse);
              if (!updates) {
                this.state.clipboardWarning = true;
                setTimeout(() => {
                  this.state.clipboardWarning = false;
                }, 3000);
              } else if (!isEmpty(updates)) {
                this.state.loading = true;
                this.$emit('update', updates);
              }
            } catch (error) {
              console.error(error);
              alert('Error when pasting nodes from clipboard: ' + error.message);
            }
          },
          Undo: () => {
            this.undo();
          },
          Redo: () => {
            this.redo();
          },
        },
        nodeItems: node => {
          const deleteNode = () => {
            if (this.editor.selected.list) {
              this.editor.selected.list.forEach(selectedNode => {
                this.editor.removeNode(selectedNode);
              })
            }
            // In case the node that was clicked on was not
            // part of the selected nodes
            if (this.editor.nodes.includes(node)) {
              this.editor.removeNode(node);
            }
          }
          if (node.name === 'EffectChain') {
            if (this.readonly) {
              return {
                View: () => {
                  this.openEffectChain(node);
                },
              };
            }
            const ecHasUpdates = !!(caseConf.outdatedEcNodes || []).find(outdatedEcNode => outdatedEcNode.id === node.id);
            return {
              Edit: () => {
                this.openEffectChain(node);
              },
              Update: ecHasUpdates ?
                () => {
                  this.resetEffectChain(node);
                }
                : false,
              Copy: () => {
                copyNodesToClipboard(editor, this.caseConf);
                this.sendAnalytics('Authoring Tool', 'copy_nodes')
              },
              // Hack to put the Delete after the Clone
              // we disable the context menu internal Delete
              // item and add our own
              'Delete': false,
              'Delete ': deleteNode,
            };
          }
          return {
            Copy: () => {
              copyNodesToClipboard(editor, this.caseConf);
              this.sendAnalytics('Authoring Tool', 'copy_nodes')
            },
            // Hack to put the Delete after the Clone
            // we disable the context menu internal Delete
            // item and add our own
            'Delete': false,
            'Delete ': deleteNode,
          };
        },
        searchBar: false
      });
      editor.use(NodesBBoxPlugin);
      editor.use(AreaPlugin, {
        background: false,
        snap: true,
        scaleExtent: { min: 0.1, max: 1 },
        translateExtent: {
          get width() {
            return editor.maxNodesBboxWidth;
          }, get height() {
            return editor.maxNodesBboxHeight;
          },
        },
      });
      editor.use(ReadonlyPlugin, { enabled: false });
      editor.use(IsolatedIdPlugin);
      editor.use(LassoSelectPlugin);
      if (this.availableNodes.includes('Group')) {
        editor.use(GroupPlugin);
      }

      // Add LOD (Level of Detail) plugin for performance optimization
      editor.use(LodPlugin, {
        simplifyThreshold: 0.2,  // Simplify nodes when zoom < 40%
        hideThreshold: 0.15,     // Hide nodes when zoom < 15%
        viewportPadding: 600,    // Render nodes 300px outside viewport
        enableViewportCulling: true,
        enableZoomSimplification: true,
        excludeFromSimplification: ['Group', 'Frame'] // Never simplify groups and frames
      });


      const actionComponent = new ActionComponent(editor);
      const effectComponent = new EffectComponent(editor);
      const checkComponent = new CheckComponent(editor);
      const timerComponent = new TimerComponent(editor);
      const effectChainComponent = new EffectChainComponent(editor);
      const frameComponent = new FrameComponent(editor);
      const commentComponent = new CommentComponent(editor);

      const components = [
        actionComponent,
        effectComponent,
        effectChainComponent,
        timerComponent,
        checkComponent,
        commentComponent,
        frameComponent,
      ];

      components.forEach(component => {
        if (this.availableNodes.includes(component.name)) {
          editor.register(component);
        }
      });

      this.setReadOnly(this.readonly);
      editor.isGlobal = this.isGlobal;

      // editor.on('process', debounce(() => {
      //   this.$nextTick(() => {
      //     this.setReadOnly(this.readonly);
      //   });
      // }, 500));

      editor.use(ConnectionPathPlugin, this.getLineTypeOptions(this.state.lineType));

      editor.on('connectioncreated', ({ output, input }) => {
        invoke(input, 'node.vueContext.onConnectionChanged');
        invoke(output, 'node.vueContext.onConnectionChanged');
      });
      editor.on('connectionremove', () => {
        if (!this.canEdit) {
          // Prevent removing connections
          return false;
        }
        return true;
      });
      editor.on('connectionremoved', ({ output, input }) => {
        invoke(input, 'node.vueContext.onConnectionChanged');
        invoke(output, 'node.vueContext.onConnectionChanged');
      });
      editor.on('renderconnection', ({ el, connection }) => {
        // eslint-disable-next-line no-param-reassign
        connection._el = el;
        el.classList.add('connection');

        // click on path line to remove connection
        const path = el.querySelector('path');
        path.addEventListener('click', () => editor.removeConnection(connection));
      });
      editor.on('click', () => {
        if (this.isToolActive('pointer')) {
          $('.case-logic .node').removeClass('selected');
        }
      });
      editor.on('nodeselect', (node) => {
        if (node.event && node.event.buttons > 3) {
          return;
        }
        if (this.isToolActive('grab')) {
          return;
        }
        $('.case-logic .node').removeClass('selected');
      });
      editor.on('nodeselected', (node) => {
        $('.case-logic .node').removeClass('selected');
        $('.case-logic .connection').removeClass('active');
        editor.selected.each(node => {
          node.vueContext.$el.classList.add('selected');
        })
        new Map([...node.inputs, ...node.outputs]).forEach(io => {
          io.connections.forEach(nodeConnection => {
            nodeConnection._el.classList.add('active');
          });
        });
      });
      editor.on('zoom', params => {
        this.state.zoom = params.zoom;
      });
      editor.on('mousemove', ({ x, y }) => {
        // For knowing where the paste was
        mouse.x = x;
        mouse.y = y;
      });

      // We need to create an empty list of handlers for the events
      // otherwise rete complains that the event is unknown
      editor.events.disableNodeDrag = [];
      editor.events.enableNodeDrag = [];
      editor.on('disableNodeDrag', (node) => {
        this.state.dragDisabledNodes[node.id] = true;
      });
      editor.on('enableNodeDrag', (node) => {
        delete this.state.dragDisabledNodes[node.id];
      });
      editor.on('nodetranslate', ({ node }) => {
        if (this.state.dragDisabledNodes[node.id]) {
          return false;
        }
        return true;
      });
      const authorizeSelection = ({node, event}) => {
        // If using any button other than left/right buttons
        // prevent select, and thus prevent translation of node
        if (event && event.buttons > 3) {
          return false;
        }
        // Only allow dragging top right part and resizing borders of comment frames
        if (node.name === 'Frame') {
          let allow = false;
          if (!event || !event.target) {
            return false;
          }
          event.target.classList.forEach(cls => {
            if (cls === 'frame-draggable' || cls.startsWith('resizable')) {
              allow = true;
            }
          });
          if (!allow) {
            return false;
          }
        }
        // If grabbing to pan, prevent selection of node so that
        // canvas is translated instead
        if (this.isToolActive('grab')) {
          return false;
        }
        // Only allow dragging top right part of comment frames
        if (node.name === 'Frame' && !event.target.classList.contains('frame-draggable')) {
          return false;
        }
      }
      // First event prevents the node from being selected
      // Second event allows the event to be bubbled up to the canvas
      // so we can pan around when a click on a node does not result in
      // a select
      editor.on('nodeselect', (node) => authorizeSelection({node, event: node.event}));
      editor.on('selectnode', authorizeSelection);

      // Only allow panning if grabbing (hold space bar).
      editor.on('translate', () => {
        if (!(this.isToolActive('grab') && this.state.isMouseDown)) {
          return false;
        }
      });
      let highestNodeZindex = 1001;
      editor.on('rendernode', ({ el, node }) => {
        el.style.zIndex = node.zIndex;
        // Groups and frames should always be at the bottom of
        // the z-layer
        if (node.name === 'Group' || node.name === 'Frame') {
          return;
        }
        // Increase z-index when a node is clicked
        // so that dropdowns etc can display above
        // other nodes
        el.addEventListener('click', () => {
          el.style.zIndex = highestNodeZindex + 1;
          highestNodeZindex += 1;
        });
        el.style.zIndex = highestNodeZindex + 1;
        highestNodeZindex += 1;
      });

      const groupOrFrameBody = (node, event) => {
        if (node && event) {
          return (node.name === 'Group' && !event.target.classList.contains('group-draggable')) ||
                 (node.name === 'Frame' &&
                  !event.target.classList.contains('frame-draggable') &&
                  !event.target.classList.contains('title-input'));
        }
      }

      editor.on('showcontextmenu', ({ e, node }) => {
        // Display context menu on effect chains even in readonly mode
        // to give users ability to view the effect chain
        if (node && node.name === 'EffectChain') {
          return true;
        }
        if (groupOrFrameBody(node, e)) {
          // Cancel node menu and trigger main menu
          editor.trigger('contextmenu', { e })
          return false;
        }
        if (e.target.closest('.context-menu')) {
          return false;
        }
        return this.canEdit;
      });

      editor.on('nodesLassoed', async () => {
        if (this.state.groupLasso) {
          // After lassoing group, revert to pointer
          this.state.groupLasso = false;
          this.setActiveTool('pointer');
          editor.trigger('addGroup');
        }
      });

      if (this.caseConf && this.caseConf.caseLogicDescription) {
        this.state.loading = Object.keys(this.caseConf.caseLogicDescription.nodes || {}).length !== 0;
        enableHistoryTracking(false);
        await editor.fromJSON({
          ...this.caseConf.caseLogicDescription,
          id: `${editorId}@0.1.0`,
        });
        if (this.navigation && this.navigation.params && this.navigation.params.nodeId) {
          const node = this.editor.nodes.find(n => n.id === this.navigation.params.nodeId);
          AreaPlugin.zoomAt(this.editor, [node]);
          this.editor.selectNode(node);
        } else {
          AreaPlugin.zoomAt(editor, editor.nodes);
        }
      } else {
        const n1 = await actionComponent.createNode();
        n1.position = [80, 200];

        editor.addNode(n1);
      }

      // When we call `fromJSON` the history plugin tracks node adds
      // we don't want the initial case logic restore to be in the history
      editor.history.clear();

      editor.on(
        'process nodecreated noderemoved connectioncreated connectionremoved',
        () => this.debouncedUpdate(),
      );

      editor.on('nodecreated', (node) => {
        const nodeName = snakeCase(node.name);
        this.sendAnalytics('Authoring Tool', `${nodeName}_node_created`);
      });

      editor.on('nodetranslated', this.debouncedUpdate);

      editor.on('noderemoved', node => {
        node._vue.$destroy();
      });

      editor.view.area.events.rendered = [];
      editor.view.area.on('rendered', () => {
        editor.nodes.forEach(node => {
          editor.view.updateConnections({ node });
        });
        editor.on('process', nodeOrControl => {
          setTimeout(() => {
            if (nodeOrControl instanceof Rete.Node) {
              editor.view.updateConnections({ node: nodeOrControl });
            } else if (nodeOrControl instanceof Rete.Control) {
              editor.view.updateConnections({ node: nodeOrControl.parent });
            } else {
              console.error(
                'rete editor process event sent without a valid node!',
              );
            }
          }, 100);
          this.preventInputDrag();
        });
        this.preventInputDrag();
        enableHistoryTracking(true);

        // Initialize LOD settings after editor setup is complete
        this.initializeLodSettings();

        this.state.loading = false;
      });
    }, 100);

    const enableGrabOnShortcutPress = (evt) => {
      if (evt.type === 'keydown' && evt.code === 'Space') {
        if (this.state.activeTool !== 'grab') {
          this.state.spaceOrMiddleModeSwitch = true;
        }
        this.setActiveTool('grab');
      }
      if (evt.type === 'mousedown' && evt.buttons > 3) {
        if (this.state.activeTool !== 'grab') {
          this.state.spaceOrMiddleModeSwitch = true;
        }
        this.setActiveTool('grab');
      }
    }
    const disableGrabOnShortcutRelease = () => {
      if (this.state.spaceOrMiddleModeSwitch) {
        this.returnToPreviousTool();
      }
      this.state.spaceOrMiddleModeSwitch = false;
    }
    // Keydown events for the case-logic div don't seem to work, so use these
    window.addEventListener('keydown', enableGrabOnShortcutPress);
    window.addEventListener('mousedown', enableGrabOnShortcutPress);
    window.addEventListener('keyup', disableGrabOnShortcutRelease);
    window.addEventListener('mouseup', disableGrabOnShortcutRelease);
  },
  beforeDestroy() {
    this.update();
  },
  methods: {
    updateCustomStates(customStates) {
      this.state.customStates = customStates;
      this.update();
    },
    debouncedUpdate: debounce(function debouncedUpdate(...args) {
      this.update(...args);
    }, 3000),
    update() {
      this.state.lastUpdateIsInternal = true;
      if (!this.editor) {
        return;
      }
      this.$emit('update', {
        caseLogicDescription: {
          ...this.editor.toJSON(),
          customStates: this.state.customStates,
        }
      });
      this.$nextTick(() => {
        this.state.lastUpdateIsInternal = false;
      });
    },
    toggleAdditionalControlsBar() {
      this.state.additionalControlsPanelOpen = !this.state.additionalControlsPanelOpen;
    },
    toggleAdvancedControlsPanel() {
      this.state.advancedControlsPanelOpen = !this.state.advancedControlsPanelOpen;
    },
    navigateToNode(node) {
      AreaPlugin.zoomAt(this.editor, [node]);
      this.editor.selectNode(node);
    },
    openEffectChain(effectChainNode) {
      const { effectChain } = effectChainNode.data;
      if (!effectChain || !effectChain.id) {
        // eslint-disable-next-line no-alert
        alert('Please select an effect chain first');
        return;
      }
      this.state.openEffectChainNode = effectChainNode;
      this.state.effectChainModalOpened = true;
    },
    closeEffectChain() {
      this.state.effectChainModalOpened = false;
      // Allow the effect chain editor to flush its changes
      // before clearing state
      this.$nextTick(() => {
        this.state.openEffectChainNode = null;
      })
    },
    updateEffectChainNode(effectChain) {
      const { openEffectChainNode } = this.state;
      openEffectChainNode.data.effectChain = effectChain;
      this.update();
    },
    resetEffectChain(effectChainNode) {
      const { caseConf } = this;
      const outdatedEcNode = (caseConf.outdatedEcNodes || []).find(node => node.id === effectChainNode.id);
      if (!outdatedEcNode) {
        return;
      }
      const { baseEffectChain } = outdatedEcNode;
      effectChainNode.data.effectChain.nodes = baseEffectChain.nodes;
      effectChainNode.data.effectChain.updatedAt = baseEffectChain.updatedAt;
      this.update();
      caseConf.outdatedEcNodes = caseConf.outdatedEcNodes.filter(node => node.id !== effectChainNode.id);
    },
    setReadOnly(readonly) {
      this.editor.trigger('readonly', readonly);
      // const $el = $(this.$el);
      // console.log($el.find('input'));
      // if (readonly) {
      //   $el.find('input').attr('disabled', 'disabled');
      //   $el.find('.el-input, .el-switch').addClass('is-disabled');
      // } else {
      //   $el.find('input').removeAttr('disabled');
      //   $el.find('.el-input, .el-switch').removeClass('is-disabled');
      // }
    },
    preventInputDrag: debounce(function preventInputDrag() {
      if (!this.inputDragPreventListener) {
        this.inputDragPreventListener = (event) => {
          event.stopPropagation();
        }
      }

      $(this.editor.view.area.container).find('input,textarea,select,button,.md-checkbox').off('click pointerdown', this.inputDragPreventListener);
      // Prevent inputs and selects from dragging the node
      // since they are already interactable (e.g. to select input text)
      $(this.editor.view.area.container).find('input,textarea,select,button,.md-checkbox').on('click pointerdown', this.inputDragPreventListener);
    }, 100),
    isToolActive(tool) {
      return this.state.activeTool === tool;
    },
    setActiveTool(tool) {
      if (this.state.activeTool !== tool) {
        this.state.previousActiveTool = this.state.activeTool;
      }
      this.state.activeTool = tool;
      this.editor.activeTool = tool;
    },
    returnToPreviousTool() {
      this.state.activeTool = this.state.previousActiveTool;
    },
    mouseDown() {
      this.state.isMouseDown = true;
    },
    zoomFull() {
      AreaPlugin.zoomAt(this.editor, this.editor.nodes);
    },
    zoomOut() {
      this.zoomBy(-0.147);
    },
    zoomIn() {
      this.zoomBy(+0.147);
    },
    zoomBy(kDiff) {
      const { area, container } = this.editor.view;
      const rect = area.el.getBoundingClientRect();
      const ox = (rect.left - container.clientWidth / 2) * kDiff;
      const oy = (rect.top - container.clientHeight / 2) * kDiff;

      area.zoom(area.transform.k + kDiff, ox, oy);
    },
    mouseUp() {
      this.state.isMouseDown = false;
    },
    setPreference(key, value) {
      const prefsKey = 'vrpatients::caseLogic::preferences';
      const preferencesJsonString = localStorage.getItem(prefsKey);
      let preferences = {};
      try {
        preferences = preferencesJsonString ? JSON.parse(preferencesJsonString) : {};
      } catch (e) {
        if (e.name !== 'SyntaxError') {
          throw e;
        }
      }
      const updatedPreferences = {
        ...preferences,
        [key]: value
      };
      localStorage.setItem(prefsKey, JSON.stringify(updatedPreferences));
    },
    getLineTypeOptions(lineType) {
      if (lineType === 'curveLinear') {
        return {
          transformer: () => ([x1, y1, x2, y2]) => [[x1, y1], [(x1+x2)/2, y1], [(x1+x2)/2, y2], [x2, y2]],
          curve: ConnectionPathPlugin.curveLinear
        }
      } else {
        return {
          type: ConnectionPathPlugin.DEFAULT,
        }
      }
    },
    getPreference(key) {
      const prefsKey = 'vrpatients::caseLogic::preferences';
      const preferencesJsonString = localStorage.getItem(prefsKey);
      let preferences = {};
      try {
        preferences = preferencesJsonString ? JSON.parse(preferencesJsonString) : {};
      } catch (e) {
        if (e.name !== 'SyntaxError') {
          throw e;
        }
      }
      return preferences[key];
    },
    addGroup() {
      this.editor.trigger('addGroup');
    },
    undo() {
      this.editor.trigger('undo')
    },
    redo() {
      this.editor.trigger('redo')
    },
    updateLodSettings() {
      if (!this.editor || !this.editor.lodPlugin) {
        return;
      }

      if (this.state.lodEnabled) {
        this.editor.lodPlugin.updateOptions({
          enableViewportCulling: this.state.lodViewportCulling,
          enableZoomSimplification: this.state.lodZoomSimplification,
        });
        this.editor.enableLod();
      } else {
        this.editor.disableLod();
      }
    },
    initializeLodSettings() {
      // Load LOD preferences from localStorage and apply them
      this.state.lodEnabled = this.getPreference('lodEnabled') || true;
      this.state.lodViewportCulling = this.getPreference('lodViewportCulling') || true;
      this.state.lodZoomSimplification = this.getPreference('lodZoomSimplification') || true;

      // Apply the settings to the plugin
      this.updateLodSettings();
    },
    showUndoRedoWarning() {
      this.state.undoredoWarning = true;
      clearTimeout(this.undoredoWarningTimeout);
      this.undoredoWarningTimeout = setTimeout(() => {
        this.state.undoredoWarning = false;
      }, 3000);
    },
    sendAnalytics(category, event) {
      if (!this.isGlobal) {
        this.$analytics.event(category, event);
      }
    }
  },
};
</script>

<style scoped>
.case-logic-container {
  position: relative;
  display: flex;
  background: #313136;
}

.case-logic-container,
.case-logic-inner-container,
.case-logic {
  flex: 1;
  width: 100% !important;
  height: 100% !important;
  position: relative;
}

.additional-controls {
  height: 100%;
  background: #303143;
  color: white;
  border: none;
  border-radius: 0;
}

.additional-controls .md-icon-button:not(.md-primary) .md-icon {
  color: white;
}

.zoom-controls {
  position: absolute;
  right: 0;
  bottom: 0;
  display: flex;
  align-items: center;
  background-color: #303143;
  color: white;
  z-index: 1001;
}

.zoom-controls .md-icon {
  color: white !important;
}

.additional-controls >>> .el-card__body {
  height: 100%;
  display: flex;
  flex-direction: row;
  padding: 0;
}
.additional-controls-content {
  height: 100%;
  display: flex;
  flex-direction: column;
  padding: 10px 3px;
}

.additional-controls-collapser {
  border: solid 1px white;
  border-radius: 0;
}

.additional-controls-collapser .md-button {
  height: 100%;
  border-radius: 0;
  margin: 0;
}

.additional-controls .custom-states-panel {
  flex: 2;
  overflow-y: hidden;
}

.additional-controls .case-logic-browser {
  flex: 1;
  overflow-x: hidden;
  overflow-y: auto;
}

.additional-controls .settings {
  display: flex;
  flex-direction: column;
  padding: 0 10px;
}

.additional-controls .settings .md-radio {
  margin: 5px 0;
}

.additional-controls .settings .line-type {
  width: 35px;
}

.separator {
  margin: 10px 0;
  height: 0.5px;
  background-color: #c0ccda;
}

.effect-chain-dialog {
  width: 90%;
  height: 90%;
  max-width: 90%;
  max-height: 90%;
}

.loading-panel {
  display: flex;
  justify-content: center;
  align-items: center;
  background: rgba(0, 0, 0, 0.3);
}

/* Hardware-accelerated spinner using CSS transforms */
.hardware-accelerated-spinner {
  display: flex;
  justify-content: center;
  align-items: center;
}

.spinner-ring {
  width: 50px;
  height: 50px;
  border: 5px solid #f3f3f3;
  border-top: 5px solid #3498db;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  will-change: transform; /* Enable hardware acceleration */
}

@keyframes spin {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
}

.case-logic >>> .context-menu {
  border: solid 1px #8f8f8f;
  border-radius: 8px;
  padding: 0;
}

.case-logic >>> .context-menu .item {
  background: rgb(245, 245, 245);
  border: none;
  color: #2b2b2b;
  font-size: 14px;
  padding: 5px 14px;
}

.case-logic >>> .context-menu .item:nth-child(1),
.case-logic >>> .context-menu .item:nth-child(3),
.case-logic >>> .context-menu .item:nth-child(5),
.case-logic >>> .context-menu .item:nth-child(8) {
  border-bottom: solid 1px #8f8f8f;
}

.case-logic >>> .context-menu .item:hover {
  background: rgba(245, 245, 245, 0.7);
}

.case-logic >>> .connection path {
  stroke: #707070;
}

.case-logic >>> .connection.active path {
  stroke: rgba(111, 184, 70, 0.8);
}

.case-logic >>> .socket {
  position: relative;
}

.case-logic.active-tool-lasso {
  cursor: crosshair;
}

.case-logic.active-tool-grab {
  cursor: grab;
}

.case-logic.active-tool-grabbing {
  cursor: grabbing;
}

.case-logic >>> .lasso {
  border-color: white !important;
}

.caseLogicWarning {
  margin-top: 10px;
  margin-left: 10px;
}

.popup-warning {
  border-radius: 3px;
  background-color: #f2994a;
  color: white;
  padding: 6px 8px;
  position: absolute;
  left: 10px;
  top: 10px;
}

.fade-enter-active,
.fade-leave-active {
  transition: all 0.3s ease;
}
.fade-enter {
  opacity: 0;
}
.fade-leave-to {
  opacity: 0;
}
</style>
